
import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { Router } from '@angular/router';

import { of, Subscription, BehaviorSubject, forkJoin } from 'rxjs';
import { filter, tap } from 'rxjs/operators';

import { DistributionReportStat } from '../../../model/statisticsReport/distribution-report-stat-api.model';
import { TabsStyleTypesEnum } from '../../../../Layout/enums/tabs-types.enum';
import { ButtonTypesEnum } from '../../../../Forms/enums/button-types.enum';
import { IndividualRecipientReportDataModel, RecipientReportDataModel } from './recipient.report.data.model';
import { messageSeverityEnum } from '../../../../Misc/enums/message-severity.enum';

import { GaugeChartModel } from '../../../../Charts/models/gaugeChart/gauge-chart.model';
import { LocalizationService } from '../../../../../Core/Localization/services/localization.service';
import { CurrentUserStateService } from '../../../../../Core/Users/services/current-user-state.service';
import { GaugeChartOptionsModel } from '../../../../Charts/models/gaugeChart/gauge-chart-options.model';
import { DistributionReportStatisticTypesEnum } from '../../../enum/distribution-report-statistic-types.enum';
import { chartColors } from '../../../../Charts/enums/chart-colors.enum';
import { ActionKeys } from '../../../../Forms/enums/keys.enum';
import { StatisticsReportService } from '../../../services/statistics.report.service';
import { StatisticsResourceStatus } from '../../../enum/statistics-resource-status.enum';
import { AuditableModel } from '../../../../Misc/models/auditable.model';
import { StatTypeChangeInterface } from '../../../model/statisticsReport/stat-type-change.interface.model';
import { DistributionModel } from '@Discover/Targeting/models/distribution.model';
import { DistributionService } from '@Common/TargetingShared/services/distribution.service';
import { ConfigService } from '@Core/Config/services/config.service';
import { FullProfileAnimationModel } from '../../../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../../Core/Dialogs/enums/overlay-animation.enum';
import { CoreDialogService } from '../../../../../Core/Dialogs/services/dialog.service';
import {
    NegativeRecipientSearchService
} from '../../../../../Discover/Targeting/components/Distribution/RemoveFromListsDialog/negative-recipient-search.service';
import { RecipientNegativeoutcomeStatsModel } from '../../../../../Discover/Targeting/models/recipients-negativeoutcome-stats.model';
import {
    RemoveFromListsDialogComponent
} from '../../../../../Discover/Targeting/components/Distribution/RemoveFromListsDialog/remove-from-lists-dialog.component';
import { DataConnectionService } from '@Core/Users/services/data-connection.service';

@Component({
    selector: 'agpr-recipient-report',
    templateUrl: './recipient.report.component.html',
    styleUrls: ['./recipient.report.component.scss', '../report.placeholder.scss'],
    standalone: false
})
export class RecipientReportComponent implements OnDestroy {
    readonly tabsStyleTypes = TabsStyleTypesEnum;
    readonly buttonTypes = ButtonTypesEnum;
    readonly statTypes = DistributionReportStat;
    readonly statisticsResourceStatus = StatisticsResourceStatus;
    readonly messageSeverities = messageSeverityEnum;

    @Input() status: StatisticsResourceStatus;
    @Input() clickTracking: boolean = true;
    @Input() openTracking: boolean = true;
    @Input() replyTracking: boolean = true;
    @Input() isScheduled: boolean = true;
    @Input() sentDate: Date;
    @Input() recipientTotal: number;
    @Input() selectedType: DistributionReportStat;
    @Input() selectedIndividualRecipient: IndividualRecipientReportDataModel;
    @Input() set targetingLists(items: AuditableModel[]) {
        if (items?.length) {
            this._targetingLists = items;
            this.fillRecipientNames();
        }
    };
    @Input() set targetingSearches(items: AuditableModel[]) {
        if (items) {
            this._targetingSearches = items;
            this.fillRecipientNames();
        }
    };
    @Input() placeholderTitle: string = 'Discover.distributionReport.reportPlaceholder.sent';
    @Input() dataNotAvailable: boolean = false;
    @Input() isRSSFeed: boolean = false;

    @Output() statTypeSelected: EventEmitter<StatTypeChangeInterface> = new EventEmitter<StatTypeChangeInterface>();

    overlayContainer: Element;

    _targetingLists: AuditableModel[];
    _targetingSearches: AuditableModel[];

    private subscriptions: Subscription[] = [];

    recipientReportData: RecipientReportDataModel;
    rrRecipientData: IndividualRecipientReportDataModel[];
    listRecipientData: IndividualRecipientReportDataModel[];
    searchRecipientData: IndividualRecipientReportDataModel[];
    additionalRecipientData: IndividualRecipientReportDataModel[];

    distribution: DistributionModel;

    rrTotalRecipients: number = 0;
    listTotalRecipients: number = 0;
    searchTotalRecipients: number = 0;
    additionalTotalRecipients: number = 0;
    data: {
        deliveredCount: number;
        openCount: number;
        clickCount: number;
        deliveredTooltip: string;
        openTooltip: string;
        clickTooltip: string;
    } = {
        deliveredCount: 0,
        openCount: 0,
        clickCount: 0,
        deliveredTooltip: null,
        openTooltip: null,
        clickTooltip: null
    };


    deliveredChart: GaugeChartModel;
    openChart: GaugeChartModel;
    clickChart: GaugeChartModel;
    replyChart: GaugeChartModel;

    userTimeZone: string;

    includeThirdPartyFeatures: boolean = false;

    @Output() mppEstimateChanged = new EventEmitter<boolean>();

    _enableMppEstimates: boolean;
    @Input() public set enableMppEstimates(value: boolean) {
        if (value !== this._enableMppEstimates) {
            this._enableMppEstimates = value;
        }
        this.distributionReportService.setMppEstimates(this._enableMppEstimates);
        this.setUniqueOpensCount();
    }
    userPrefsMppEstimates: boolean = false;
    uniqueOpensCount: number = 0;

    isDistribution: boolean = false;

    hasNegativeOutcome: boolean = false;

    constructor(
        public coreDialogService: CoreDialogService,
        public localizationService: LocalizationService,
        public distributionReportService: StatisticsReportService,
        public currentUserStateService: CurrentUserStateService,
        public distributionService: DistributionService,
        public dataConnectionService: DataConnectionService,
        private router: Router,
        private configService: ConfigService,
        public negativeRecipientSearchService: NegativeRecipientSearchService
    ) {
        this.isDistribution = this.router.url.includes('distributions');
        this.overlayContainer = document.getElementById('main-content');
        this.subscriptions.push(
            this.distributionService.getFocusedResource().pipe(filter(x => !!x)).subscribe(x => {
                this.distribution = x;
            }),
            this.distributionReportService.recipientReportData$.pipe(
                tap(x => {
                    this.resetRecipientCounts();
                    this.recipientReportData = x;

                    this.rrRecipientData = x?.individualRecipientReports.filter(ind =>
                        !!ind.listId && ind.listId === this.distribution.recommendedRecipientsListId
                    );
                    this.listRecipientData = x?.individualRecipientReports.filter(ind =>
                        !!ind.listId && ind.listId !== this.distribution.recommendedRecipientsListId
                    );
                    this.searchRecipientData = x?.individualRecipientReports.filter(ind => !!ind.searchId);
                    this.additionalRecipientData = x?.individualRecipientReports.filter(ind => !ind.listId && !ind.searchId);

                    // NOTE : this isn't accurate if there's duplicate emails in different lists
                    // The backend would have to give us this total.
                    this.rrRecipientData?.forEach(y => {
                        y.isRecommendedRecipient = true;
                        this.rrTotalRecipients += y.recipientCount;
                    });
                    this.listRecipientData?.forEach(y => this.listTotalRecipients += y.recipientCount);
                    this.searchRecipientData?.forEach(y => this.searchTotalRecipients += y.recipientCount);
                    this.additionalRecipientData?.forEach(y => this.additionalTotalRecipients += y.recipientCount);

                    if (x?.isBriefingReport) {
                        this.listRecipientData = [
                            ...(x.mailingListReports ?? []),
                            ...(x.standaloneRecipientReport ? [x.standaloneRecipientReport] : [])
                        ];
                        this.listTotalRecipients = 0;
                        this.listRecipientData.forEach(y => this.listTotalRecipients += y.recipientCount);

                        this.additionalRecipientData = [];
                        this.additionalTotalRecipients = 0;
                    }

                    this.fillRecipientNames();
                    this.setUniqueOpensCount();
                })
            ).subscribe(() => {
                this.buildCharts();
                this.getNegativeOutcomeListStats();
                this.setNegativeOutcomeRecipientStats();
            }),
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(prefs => {
                this.userTimeZone = prefs.timeZone;
            }),
        );
    }

    resetRecipientCounts() {
        this.rrTotalRecipients = 0;
        this.listTotalRecipients = 0;
        this.searchTotalRecipients = 0;
        this.additionalTotalRecipients = 0;
    }

    ngOnDestroy() {
        this.subscriptions.forEach(
            subscription => subscription.unsubscribe()
        );
    }

    navigateToLearnMore() {
        window.open('https://www.agilitypr.com/contact/');
    }

    fillRecipientNames() {
        if (this._targetingLists && this.listRecipientData) {
            this.listRecipientData.forEach(x => x.recipientName = this._targetingLists.find(list => list.id === x.listId)?.name);
        }
        if (this._targetingSearches && this.searchRecipientData) {
            this.searchRecipientData.forEach(x => x.recipientName = this._targetingSearches.find(search => search.id === x.searchId)?.name);
        }
    }

    selectStatType(statType: DistributionReportStat, data?: IndividualRecipientReportDataModel) {
        this.selectedType = statType;

        this.statTypeSelected.emit({
            statType: this.selectedType,
            data
        });
    }

    buildCharts() {
        if (!this.recipientReportData) {
            return;
        }

        this.setDeliveredChartModel();
        this.setOpenChartModel();
        this.setClickChartModel();
        this.setReplyChartModel();
        this.setTooltips();
    }

    /**
     * Returns chart options based on the benchmark difference provided
     */
    getChartOptions(differenceOverBenchmark?: number, statisticType?: DistributionReportStatisticTypesEnum): GaugeChartOptionsModel {
        const chartOptions: GaugeChartOptionsModel = {
            minXDegrees: -210,
            maxXDegrees: 30,
            useOneColor: true,
            percentageFontSize: 18,
            percentageFontWeight: '600',
        };

        if (differenceOverBenchmark === null || differenceOverBenchmark === undefined) {
            return chartOptions;
        }

        chartOptions.percentLabelOptions = {
            verticalCenter : 'middle'
        };

        chartOptions.seriesLabelOptions = {
            fontSize: 16,
            fill: chartColors.agprPositive,
            horizontalCenter: 'middle',
            verticalCenter: 'top',
            dy: 5
        };

        if (differenceOverBenchmark > 0) {
            chartOptions.seriesLabelOptions.tooltip = this.localizationService.transform(
                'Discover.targeting.distribution.recipient.report.benchmarkStatisticComparison.tooltip',
                {
                    'statisticType': this.localizationService.transform(`Discover.targeting.distribution.statistic.lowercase.${statisticType}`),
                    'value': `${differenceOverBenchmark}%`,
                    'description': this.localizationService.transform('Discover.targeting.distribution.recipient.report.higherThan'),
                    'benchmarkType':  this.localizationService.transform('Discover.targeting.distribution.recipient.report.average')
                }
            );
        }

        if (differenceOverBenchmark < 0) {
            chartOptions.seriesLabelOptions.fill = chartColors.agprNegative;
            chartOptions.seriesLabelOptions.tooltip = this.localizationService.transform(
                'Discover.targeting.distribution.recipient.report.benchmarkStatisticComparison.tooltip',
                {
                    'statisticType': this.localizationService.transform(`Discover.targeting.distribution.statistic.lowercase.${statisticType}`),
                    'value': `${Math.abs(differenceOverBenchmark)}%`,
                    'description': this.localizationService.transform('Discover.targeting.distribution.recipient.report.lowerThan'),
                    'benchmarkType':  this.localizationService.transform('Discover.targeting.distribution.recipient.report.average')
                }
            );
        }

        if (differenceOverBenchmark === 0) {
            chartOptions.seriesLabelOptions.fill = chartColors.agprBlack;
        }

        return chartOptions;
    }

    /**
     * Returns series label
     */
    getSeriesLabel(differenceOverBenchmark?: number): string {
        if (differenceOverBenchmark > 0) {
            return `${differenceOverBenchmark}%`;
        }

        if (isNaN(differenceOverBenchmark)) {
            return '-- %';
        }

        if (differenceOverBenchmark < 0) {
            return `${Math.abs(differenceOverBenchmark)}%`;
        }
        if (differenceOverBenchmark !== undefined && differenceOverBenchmark !== null) {
            return `${differenceOverBenchmark}%`;
        }


        return '';
    }

    /**
     * Sets the chart for delivery rate
     */
    setDeliveredChartModel() {
        const options = this.getChartOptions(
            this.recipientReportData.deliveredRatioPercentageChangeFromBenchmark,
            DistributionReportStatisticTypesEnum.DELIVERY_RATE
        );
        if (options.seriesLabelOptions) {
            options.seriesLabelOptions.tooltipPointerOrientation = 'up';
            options.seriesLabelOptions.tooltipDy = 15;
        }

        this.data.deliveredCount = parseFloat(this.getSeriesLabel(this.recipientReportData.deliveredRatioPercentageChangeFromBenchmark));

        this.deliveredChart = new GaugeChartModel([
            {
                label: '',
                dataProvider: of(
                    [
                        {
                            xValue: this.recipientReportData.deliveredRatio * 100
                        }
                    ]
                )
            }
        ], options);
    }

    /**
     * Sets the chart for the open rate
     */
    setOpenChartModel() {
        const options = this.getChartOptions(
            this.recipientReportData.uniqueOpenRatioPercentageChangeFromBenchmark,
            DistributionReportStatisticTypesEnum.UNIQUE_OPEN_RATE
        );
        if (options.seriesLabelOptions) {
            options.seriesLabelOptions.tooltipPointerOrientation = 'up';
            options.seriesLabelOptions.tooltipDy = 15;
        }

        this.data.openCount = parseFloat(this.getSeriesLabel(this.recipientReportData.uniqueOpenRatioPercentageChangeFromBenchmark));
        options.notAvailable = !this.openTracking;

        this.openChart = new GaugeChartModel([
            {
                label: '',
                dataProvider: of(
                    [
                        {
                            xValue: this.recipientReportData.uniqueOpenRatio * 100
                        }
                    ]
                )
            }
        ], options);
    }

    /**
     * Sets the chart for the click rate
     */
    setClickChartModel() {
        const options = this.getChartOptions(
            this.recipientReportData.uniqueClickRatePercentageChangeFromBenchmark,
            DistributionReportStatisticTypesEnum.UNIQUE_CLICK_RATE
        );
        if (options.seriesLabelOptions) {
            options.seriesLabelOptions.tooltipPointerOrientation = 'up';
            options.seriesLabelOptions.tooltipDy = 15;
        }
        options.notAvailable = !this.clickTracking;
        this.data.clickCount = parseFloat(this.getSeriesLabel(this.recipientReportData.uniqueClickRatePercentageChangeFromBenchmark));

        this.clickChart = new GaugeChartModel([
            {
                label: '',
                dataProvider: of(
                    [
                        {
                            xValue: this.recipientReportData.uniqueClickRatio * 100
                        }
                    ]
                )
            }
        ], options);
    }

    setUniqueOpensCount() {
        if (this.recipientReportData) {
            if (this._enableMppEstimates) {
                this.uniqueOpensCount = this.recipientReportData.estimatedActualUniqueOpens;
            } else {
                this.uniqueOpensCount = this.recipientReportData.uniqueOpenCount;
            }
        }
        this.mppEstimateChanged.next(this._enableMppEstimates);
    }

    /**
     * Sets the chart for the open rate
     */
    setReplyChartModel() {
        const options = this.getChartOptions(
            this.recipientReportData.replyRatioPercentageChangeFromBenchmark,
            DistributionReportStatisticTypesEnum.REPLY_RATE
        );
        if (options.seriesLabelOptions) {
            options.seriesLabelOptions.tooltipPointerOrientation = 'up';
            options.seriesLabelOptions.tooltipDy = 15;
        }
        options.notAvailable = !this.replyTracking;

        this.replyChart = new GaugeChartModel([
            {
                label: '',
                dataProvider: of(
                    [
                        {
                            label: this.getSeriesLabel(this.recipientReportData.replyRatioPercentageChangeFromBenchmark),
                            xValue: this.recipientReportData.replyRatio * 100
                        }
                    ]
                )
            }
        ], options);
    }

    private getLocalizedTooltipForBenchmarkComparison(differenceOverBenchmark?: number, statisticType?: DistributionReportStatisticTypesEnum): string {
        let description = this.localizationService.transform('Discover.targeting.distribution.recipient.report.lowerThan');
        if (differenceOverBenchmark > 0) {
            description = this.localizationService.transform('Discover.targeting.distribution.recipient.report.higherThan');
        }

        return this.localizationService.transform(
            'Discover.targeting.distribution.recipient.report.benchmarkStatisticComparison.tooltip',
            {
                'statisticType': this.localizationService.transform(`Discover.targeting.distribution.statistic.lowercase.${statisticType}`),
                'value': `${Math.abs(differenceOverBenchmark)}%`,
                'description': description,
                'benchmarkType':  this.localizationService.transform('Discover.targeting.distribution.recipient.report.average')
            }
        );
    }

    private setTooltips() {
        this.data.deliveredTooltip = this.getLocalizedTooltipForBenchmarkComparison(
            parseFloat(this.getSeriesLabel(this.recipientReportData.deliveredRatioPercentageChangeFromBenchmark)),
            DistributionReportStatisticTypesEnum.DELIVERY_RATE
        );

        this.data.openTooltip = this.getLocalizedTooltipForBenchmarkComparison(
            this.recipientReportData.uniqueOpenRatioPercentageChangeFromBenchmark,
            DistributionReportStatisticTypesEnum.UNIQUE_OPEN_RATE
        );

        this.data.clickTooltip = this.getLocalizedTooltipForBenchmarkComparison(
            this.recipientReportData.uniqueClickRatePercentageChangeFromBenchmark,
            DistributionReportStatisticTypesEnum.UNIQUE_CLICK_RATE
        );
    }

    statCardKeydown(event: KeyboardEvent, statType: DistributionReportStat) {
        if (event.key === ActionKeys.ENTER || event.key === ActionKeys.SPACE) {
            this.selectStatType(statType);
        }
    }

    setNegativeOutcomeRecipientStats(): void {
        if (this.recipientReportData) {
            const {
                unsubscribed,
                previousUnsubscribe,
                hardBounced,
                previousHardBounced,
                spamReport
            } = this.recipientReportData;

            const stats = new RecipientNegativeoutcomeStatsModel(
                unsubscribed,
                previousUnsubscribe,
                hardBounced,
                previousHardBounced,
                spamReport
            );
            this.negativeRecipientSearchService.addDeliveryStatusFilterOptions(stats);
        }
    }

    getNegativeOutcomeListStats(): void {
        this.subscriptions.push(this.negativeRecipientSearchService.negativeOutcomeListStats$.subscribe(
            (negativeOutcome: RecipientNegativeoutcomeStatsModel) => {
                const total = Object.values(negativeOutcome).reduce((sum, count) => sum + count, 0);
                this.hasNegativeOutcome = total > 0;
                if (this.recipientReportData) {
                    this.recipientReportData.negativeOutcome = negativeOutcome;
                }

            }
        ));
    }

    openRemoveFromListsDialog(): void {
        this.coreDialogService.open(RemoveFromListsDialogComponent, {
            inputs: {
                overlayContainer: this.overlayContainer,
                animationTrigger: new BehaviorSubject(
                    new FullProfileAnimationModel(
                        FullProfileDialogAnimationsEnum.slideInOutAnimation,
                        OverlayAnimationStateEnum.open
                    )
                )
            },
            outputs: {
                dialogClose: (isRemoved: boolean) => {
                    if(isRemoved) {
                        this.negativeRecipientSearchService.getNegativeOutcomeLists();
                    }
                }
            }

        });
    }
}
